import { Link } from '@tanstack/react-router';
import { BadgeDollarSign, Unplug } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import type { SocialConnection } from '@nucleus-commerce/api-client/admin';
import { Button } from '@nucleus-commerce/ui/components/button';

import { hasAdsScopes } from '../schemas/channel-state';

import { ChannelStep, StepBadge, StepRow } from './channel-step';

export interface AdsStepProps {
  storeId: string;
  /** False until the network is connected. */
  active: boolean;
  connection: SocialConnection | undefined;
  editable: boolean;
  connecting: boolean;
  onConnectAds: () => void;
  onChoose: () => void;
  disabling: boolean;
  onDisable: () => void;
}

/** Step 3: ads consent, then the account pick; enabled means conversions flow and snapshots run. */
export function AdsStep({
  storeId,
  active,
  connection,
  editable,
  connecting,
  onConnectAds,
  onChoose,
  disabling,
  onDisable,
}: AdsStepProps) {
  const { t } = useTranslation('social');
  const consented = active && hasAdsScopes(connection);
  const enabled = consented && connection?.ads.enabled === true;

  const badge = !active ? (
    <StepBadge tone="todo">{t('steps.waiting')}</StepBadge>
  ) : enabled ? (
    <StepBadge tone="done">{t('status.connected')}</StepBadge>
  ) : (
    <StepBadge tone="todo">{t('status.disconnected')}</StepBadge>
  );

  return (
    <ChannelStep index={4} title={t('ads.title')} badge={badge} disabled={!active}>
      <p className="text-muted-foreground text-sm">{t('ads.hint')}</p>
      {!active ? null : !consented ? (
        <>
          <p className="text-muted-foreground text-xs">{t('ads.consentNote')}</p>
          {editable ? (
            <div>
              <Button type="button" size="sm" disabled={connecting} onClick={onConnectAds}>
                <BadgeDollarSign aria-hidden />
                {connecting ? t('ads.connecting') : t('ads.connect')}
              </Button>
            </div>
          ) : null}
        </>
      ) : !enabled ? (
        <>
          <p className="text-muted-foreground text-xs">{t('ads.pickHint')}</p>
          {editable ? (
            <div>
              <Button type="button" size="sm" onClick={onChoose}>
                {t('ads.choose')}
              </Button>
            </div>
          ) : null}
        </>
      ) : (
        <>
          <dl className="grid gap-3 sm:grid-cols-2">
            <StepRow label={t('ads.account')}>
              <span className="font-medium">{connection.ads.adAccountName ?? '—'}</span>
              {connection.ads.adAccountId ? (
                <span className="text-muted-foreground block font-mono text-xs">
                  {t('ads.accountId', { id: connection.ads.adAccountId })}
                </span>
              ) : null}
            </StepRow>
            <StepRow label={t('ads.conversions')}>
              <span className="block">{t('ads.conversionsOn')}</span>
              <Link
                to="/s/$storeId/settings/analytics"
                params={{ storeId }}
                className="text-primary text-xs underline-offset-4 hover:underline"
              >
                {t('ads.destinations')}
              </Link>
            </StepRow>
          </dl>
          <p className="text-muted-foreground text-xs">{t('ads.snapshots')}</p>
          <p className="text-muted-foreground text-xs">{t('ads.paidNote')}</p>
          {editable ? (
            <div className="flex flex-wrap gap-2">
              <Button type="button" variant="outline" size="sm" onClick={onChoose}>
                {t('ads.change')}
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={disabling}
                onClick={onDisable}
              >
                <Unplug aria-hidden />
                {disabling ? t('ads.disabling') : t('ads.disable')}
              </Button>
            </div>
          ) : null}
        </>
      )}
    </ChannelStep>
  );
}
